import { useEffect, useSyncExternalStore } from "react";
import { useWebSocketStore } from "../store/websocketStore";
import useGetTransferToken from "../hooks/useGetTransferToken";
import { authService } from "../services/authService";

// Reactive token check - updates immediately when token changes
const useHasValidToken = () =>
  useSyncExternalStore(
    (cb) => authService.subscribe(cb),
    () => authService.isAuthenticated()
  );

export const WebSocketManager = () => {
  // Use direct token check instead of useIsAuthenticated to avoid race conditions
  // during logout (useIsAuthenticated has async delay via authProvider.check)
  const hasValidToken = useHasValidToken();

  const { transferToken, refetch } = useGetTransferToken({
    enabled: hasValidToken,
  });

  const setWsToken = useWebSocketStore((state) => state.setToken);
  const needsNewToken = useWebSocketStore((state) => state.needsNewToken);

  // Update token in store whenever we get a new one
  useEffect(() => {
    if (hasValidToken && transferToken?.transfer_token) {
      setWsToken(transferToken.transfer_token);
    } else if (!hasValidToken) {
      setWsToken(null);
    }
  }, [transferToken, setWsToken, hasValidToken]);

  // Request new token when store signals it's needed
  useEffect(() => {
    if (needsNewToken && hasValidToken) {
      refetch().catch((error) => {
        console.error("[WebSocketManager] Failed to refresh token:", error);
      });
    }
  }, [needsNewToken, hasValidToken, refetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setWsToken(null);
    };
  }, [setWsToken]);

  return null;
};
